import { copyImage, uploadImage } from "../files";
import { Database } from "types/supabase";
import { supabaseClient } from "supabase";

export type createEventParams = {
  title: Database["public"]["Tables"]["events"]["Row"]["title"];
  sub_title: Database["public"]["Tables"]["events"]["Row"]["sub_title"];
  description: Database["public"]["Tables"]["events"]["Row"]["description"];
  title_pt: Database["public"]["Tables"]["events"]["Row"]["title_pt"];
  sub_title_pt: Database["public"]["Tables"]["events"]["Row"]["sub_title_pt"];
  description_pt: Database["public"]["Tables"]["events"]["Row"]["description_pt"];
  price: Database["public"]["Tables"]["events"]["Row"]["price"];
  price_pt: Database["public"]["Tables"]["events"]["Row"]["price_pt"];
  start_time: Database["public"]["Tables"]["events"]["Row"]["start_time"];
  finish_time: Database["public"]["Tables"]["events"]["Row"]["finish_time"];
  currPicture: Database["public"]["Tables"]["events"]["Row"]["picture"];
  newPicture?: File;
  organizer_id: Database["public"]["Tables"]["events"]["Row"]["organizer_id"];
  venue: Database["public"]["Tables"]["events"]["Row"]["venue"];
  venue_name: Database["public"]["Tables"]["events"]["Row"]["venue_name"];
  venue_address: Database["public"]["Tables"]["events"]["Row"]["venue_address"];
  venue_city: Database["public"]["Tables"]["events"]["Row"]["venue_city"];
  city_id: Database["public"]["Tables"]["events"]["Row"]["city_id"];
  venue_description: Database["public"]["Tables"]["events"]["Row"]["venue_description"];
  venue_location_lat: Database["public"]["Tables"]["events"]["Row"]["venue_location_lat"];
  venue_location_long: Database["public"]["Tables"]["events"]["Row"]["venue_location_long"];
  published: Database["public"]["Tables"]["events"]["Row"]["published"];
  cancelled: Database["public"]["Tables"]["events"]["Row"]["cancelled"];
  tags: Database["public"]["Tables"]["tags"]["Row"]["id"][];
  main_tag: Database["public"]["Tables"]["events"]["Row"]["main_tag"];
  is_recommended: Database["public"]["Tables"]["events"]["Row"]["is_recommended"];
  registration_link: Database["public"]["Tables"]["events"]["Row"]["registration_link"];
  made_by_organizer?: boolean | null;
  source?: Database["public"]["Tables"]["events"]["Row"]["source"];
  source_url?: Database["public"]["Tables"]["events"]["Row"]["source_url"];
  source_uid?: Database["public"]["Tables"]["events"]["Row"]["source_uid"];
  source_application_id?: Database["public"]["Tables"]["events"]["Row"]["source_application_id"];
};

export const createEvent = async ({
  title,
  sub_title,
  description,
  title_pt,
  sub_title_pt,
  description_pt,
  price,
  price_pt,
  start_time,
  finish_time,
  currPicture,
  newPicture,
  organizer_id,
  venue_address,
  venue_city,
  city_id,
  venue_description,
  venue_location_lat,
  venue_location_long,
  venue_name,
  venue,
  published,
  cancelled,
  tags,
  main_tag,
  is_recommended,
  registration_link,
  made_by_organizer,
  source,
  source_url,
  source_uid,
  source_application_id,
}: createEventParams): Promise<Database["public"]["Tables"]["events"]["Row"]> => {
  const createdEvent = {
    title,
    sub_title,
    organizer_id,
    description,
    title_pt,
    sub_title_pt,
    description_pt,
    price: price === "" ? null : price,
    price_pt: price_pt === "" ? null : price_pt,
    start_time,
    finish_time,
    venue_address,
    venue_city,
    city_id,
    venue_description,
    venue_location_lat,
    venue_location_long,
    venue_name,
    venue,
    published,
    cancelled,
    tags,
    main_tag,
    is_recommended,
    registration_link,
    made_by_organizer,
    source: source ?? null,
    source_url: source_url ?? null,
    source_uid: source_uid ?? null,
    source_application_id: source_application_id ?? null,
  };

  const { error, data } = await supabaseClient
    .from("events")
    .insert(createdEvent)
    .select();

  if (error) {
    throw error;
  }

  if (!data || data.length === 0) {
    throw new Error("Event insert returned no row");
  }

  const insertedEvent = data[0];

  // taking care of the main picture
  let picturePath = null;
  if (newPicture || currPicture) {
    if (currPicture && !newPicture) {
      picturePath = await copyImage(
        currPicture,
        `${Date.now()}.${currPicture.substring(
          currPicture.lastIndexOf(".") + 1
        )}`,
        "events",
        insertedEvent.uid
      );
    } else {
      picturePath = await uploadImage(
        newPicture!,
        `${Date.now()}.${newPicture!.type.split("/")[1]}`,
        "events",
        insertedEvent.uid
      );
    }
    const { error: updateError, data: updatedData } = await supabaseClient
      .from("events")
      .update({ picture: picturePath })
      .eq("id", insertedEvent.id)
      .select();

    if (updateError) {
      throw updateError;
    }

    // The caller must receive the final picture value, not the pre-update one.
    if (updatedData && updatedData.length > 0) {
      return updatedData[0];
    }
  }

  return insertedEvent;
};
